import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Separator } from '@/components/ui/shadcn/separator';
import { Switch } from '@/components/ui/shadcn/switch';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import {
    buildModules,
    FILTERS,
    getPermissionCategory,
    getPermissionDescription,
    getRoleDescription,
    MODULE_ICONS,
    type PermissionCategory,
    prettyActionLabel,
    RISKY_PERMISSIONS,
} from '@/lib/permissions/permissionModules';
import { cn } from '@/lib/utils';
import { AlertTriangle, Search, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

type RoleRecord = {
    route_key: string;
    name: string;
    permissions: string[];
};

export default function RolePermissionsManager() {
    const { t } = useTranslations();
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState<RoleRecord[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
    const [selectedRoleName, setSelectedRoleName] = useState('');
    const [draftPermissions, setDraftPermissions] = useState<string[]>([]);
    const [savedPermissions, setSavedPermissions] = useState<string[]>([]);
    const [roleSearch, setRoleSearch] = useState('');
    const [permissionSearch, setPermissionSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState<PermissionCategory>('all');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;

        fetch(route('settings.system.role-permissions'), { headers: { Accept: 'application/json' } })
            .then((response) => response.json())
            .then((payload) => {
                if (!mounted) return;

                const nextRoles = (payload.roles ?? []) as RoleRecord[];
                const nextPermissions = (payload.permissions ?? []) as string[];

                setRoles(nextRoles);
                setAvailablePermissions(nextPermissions);

                const firstRole = nextRoles[0]?.name ?? '';
                setSelectedRoleName(firstRole);
                const firstPermissions = (nextRoles[0]?.permissions ?? []) as string[];
                setDraftPermissions(firstPermissions);
                setSavedPermissions(firstPermissions);

                setLoading(false);
            })
            .catch(() => {
                toast.error(t('toasts.settings_rolepermissionsmanager.load_failed', 'Failed to load role permissions'));
                setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, []);

    const permissionModules = useMemo(() => buildModules(availablePermissions), [availablePermissions]);

    const selectedRole = useMemo(() => roles.find((role) => role.name === selectedRoleName) ?? null, [roles, selectedRoleName]);

    const filteredRoles = useMemo(() => {
        const q = roleSearch.trim().toLowerCase();
        if (!q) return roles;
        return roles.filter((role) => role.name.toLowerCase().includes(q));
    }, [roles, roleSearch]);

    const filteredModules = useMemo(() => {
        const q = permissionSearch.trim().toLowerCase();

        return permissionModules
            .map((module) => {
                const permissions = module.permissions.filter((permission) => {
                    const matchesQuery = !q || permission.toLowerCase().includes(q) || prettyActionLabel(permission).toLowerCase().includes(q);
                    if (!matchesQuery) return false;

                    if (activeFilter === 'all') return true;
                    if (activeFilter === 'risky') return RISKY_PERMISSIONS.has(permission) || permission.endsWith('.delete');

                    return getPermissionCategory(permission) === activeFilter;
                });

                return { ...module, permissions };
            })
            .filter((module) => module.permissions.length > 0);
    }, [permissionModules, permissionSearch, activeFilter]);

    const changedCount = useMemo(() => {
        const before = new Set(savedPermissions);
        const after = new Set(draftPermissions);
        const added = [...after].filter((permission) => !before.has(permission));
        const removed = [...before].filter((permission) => !after.has(permission));
        return added.length + removed.length;
    }, [draftPermissions, savedPermissions]);

    const hasChanges = changedCount > 0;

    const handleRolePick = (roleName: string): void => {
        const role = roles.find((entry) => entry.name === roleName);
        if (!role) return;

        setSelectedRoleName(roleName);
        setDraftPermissions(role.permissions);
        setSavedPermissions(role.permissions);
    };

    const togglePermission = (permission: string, enabled: boolean): void => {
        if (enabled) {
            if (!draftPermissions.includes(permission)) {
                setDraftPermissions((previous) => [...previous, permission]);
            }
            return;
        }

        setDraftPermissions((previous) => previous.filter((entry) => entry !== permission));
    };

    const resetChanges = (): void => {
        setDraftPermissions(savedPermissions);
    };

    const saveRolePermissions = async (): Promise<void> => {
        if (!selectedRole || !selectedRole.route_key) {
            toast.error(t('toasts.settings_rolepermissionsmanager.no_valid_role', 'No valid role selected'));
            return;
        }

        setSaving(true);
        try {
            const response = await csrfFetch(route('settings.system.role-permissions.update', { role: selectedRole.route_key }), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({ permissions: draftPermissions }),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data?.message ?? 'Failed to save role permissions');
            }

            setRoles((previous) =>
                previous.map((role) => (role.route_key === selectedRole.route_key ? { ...role, permissions: draftPermissions } : role)),
            );
            setSavedPermissions(draftPermissions);
            toast.success(
                t('toasts.settings_rolepermissionsmanager.saved', 'Saved default permissions for :name').replace(':name', String(selectedRole.name)),
            );
        } catch (error) {
            toast.error(
                error instanceof Error ? error.message : t('toasts.settings_rolepermissionsmanager.save_failed', 'Failed to save role permissions'),
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="text-muted-foreground py-8 text-sm">Loading role permissions...</CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Role Default Permissions</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)_280px]">
                    <Card className="gap-3 py-4">
                        <CardHeader className="px-4 pb-0">
                            <CardTitle className="text-base">Roles</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 px-4">
                            <div className="relative">
                                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    value={roleSearch}
                                    onChange={(event) => setRoleSearch(event.target.value)}
                                    placeholder="Search role..."
                                    className="pl-9"
                                />
                            </div>
                            <div className="space-y-2">
                                {filteredRoles.map((role) => {
                                    const isSelected = selectedRoleName === role.name;
                                    return (
                                        <button
                                            key={role.route_key}
                                            type="button"
                                            onClick={() => handleRolePick(role.name)}
                                            className={cn(
                                                'w-full rounded-lg border px-3 py-3 text-left transition-colors',
                                                isSelected ? 'border-primary bg-primary/5 ring-primary/30 ring-1' : 'hover:bg-muted/40 border-border',
                                            )}
                                        >
                                            <p className="text-sm font-semibold">{role.name}</p>
                                            <p className="text-muted-foreground text-xs">{getRoleDescription(role.name)}</p>
                                            <p className="text-muted-foreground mt-1 text-xs">{role.permissions.length} permissions</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="gap-3 py-4">
                        <CardHeader className="space-y-3 px-4 pb-0">
                            <CardTitle className="text-base">Permissions</CardTitle>
                            <div className="relative">
                                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    value={permissionSearch}
                                    onChange={(event) => setPermissionSearch(event.target.value)}
                                    placeholder="Search permissions..."
                                    className="pl-9"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {FILTERS.map((filter) => (
                                    <button
                                        key={filter.id}
                                        type="button"
                                        className={cn(
                                            'rounded-full border px-3 py-1 text-xs transition-colors',
                                            activeFilter === filter.id
                                                ? 'border-primary bg-primary text-primary-foreground'
                                                : 'text-muted-foreground hover:bg-muted/60',
                                        )}
                                        onClick={() => setActiveFilter(filter.id)}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        </CardHeader>
                        <CardContent className="px-4">
                            <div className="space-y-3">
                                {filteredModules.map((module) => {
                                    const Icon = MODULE_ICONS[module.id] ?? ShieldCheck;
                                    const enabledInModule = module.permissions.filter((permission) => draftPermissions.includes(permission)).length;

                                    return (
                                        <div key={module.id} className="rounded-xl border p-3">
                                            <div className="flex items-start gap-3 pb-3">
                                                <div className="bg-primary/10 text-primary mt-0.5 rounded-full p-2">
                                                    <Icon className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold">{module.label}</p>
                                                    <p className="text-muted-foreground text-xs">{module.description}</p>
                                                    <p className="text-primary mt-1 text-xs font-medium">
                                                        {enabledInModule} / {module.permissions.length} enabled
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                                {module.permissions.map((permission) => {
                                                    const checked = draftPermissions.includes(permission);
                                                    const isRisky = RISKY_PERMISSIONS.has(permission) || permission.endsWith('.delete');

                                                    return (
                                                        <div
                                                            key={permission}
                                                            className={cn(
                                                                'flex items-center justify-between rounded-lg border px-2.5 py-2',
                                                                checked ? 'border-primary/30 bg-primary/5' : 'border-border',
                                                            )}
                                                        >
                                                            <div className="min-w-0 pr-2">
                                                                <p className="truncate text-xs font-medium">{prettyActionLabel(permission)}</p>
                                                                {getPermissionDescription(permission) && (
                                                                    <p className="text-muted-foreground mt-0.5 line-clamp-2 text-[10px] leading-tight">
                                                                        {getPermissionDescription(permission)}
                                                                    </p>
                                                                )}
                                                                {isRisky && (
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="text-warning-subtle-foreground mt-1 px-1.5 py-0 text-[10px]"
                                                                    >
                                                                        Risky
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <Switch
                                                                checked={checked}
                                                                onCheckedChange={(next) => togglePermission(permission, Boolean(next))}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="gap-3 py-4">
                        <CardHeader className="px-4 pb-0">
                            <CardTitle className="text-base">Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 px-4">
                            <div className="rounded-lg border p-3">
                                <p className="text-sm font-semibold">{selectedRoleName || 'No role selected'}</p>
                                <p className="text-muted-foreground text-xs">Role defaults</p>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Enabled permissions</span>
                                    <span>
                                        {draftPermissions.length} / {availablePermissions.length}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Changed in this session</span>
                                    <span>{changedCount}</span>
                                </div>
                            </div>
                            <Separator />
                            <div className="space-y-2">
                                <p className="text-sm font-semibold">Warnings</p>
                                {draftPermissions.includes('settings.manage') && (
                                    <div className="border-border bg-warning-subtle text-warning-subtle-foreground flex items-start gap-2 rounded-md border p-2 text-xs">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
                                        This role can change global system settings.
                                    </div>
                                )}
                                {draftPermissions.includes('acl.manage') && (
                                    <div className="border-border bg-warning-subtle text-warning-subtle-foreground flex items-start gap-2 rounded-md border p-2 text-xs">
                                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
                                        This role can edit user permissions.
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" type="button" onClick={resetChanges} disabled={!hasChanges || saving} className="flex-1">
                                    Reset
                                </Button>
                                <Button
                                    type="button"
                                    onClick={saveRolePermissions}
                                    disabled={!hasChanges || saving || !selectedRole}
                                    className="flex-1"
                                >
                                    {saving ? 'Saving...' : 'Save role'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </CardContent>
        </Card>
    );
}
